// @ts-nocheck
// Location: src/components/admin/AddUrlDialog.tsx
// Purpose: Add URL dialog for adding URLs to a pod
// Scope: Shows URL input fields and bulk import options

/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { useMutation, useQuery, useConvex } from 'convex/react';
import { api } from '@/convex/_generated/api';
import {
	canonicalPodPlatform,
	getPodModeCapability,
	getPodPlatformLabel,
	Pod,
} from '@/types/pods';
import { Id } from '@/convex/_generated/dataModel';
import { useDropzone } from 'react-dropzone';
import { cn } from '@/lib/utils';
import { UploadIcon } from '@/components/ui/icons/upload-icon';
// import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Textarea } from '@/components/ui/textarea';
import { motion, AnimatePresence } from 'framer-motion';
import {
	extractUrlCandidatesFromText,
	parsePodTargetUrl,
} from '@/utils/podUrlParsing';

interface AddUrlDialogProps {
	pod: Pod;
	isOpen: boolean;
	onClose: () => void;
	onUrlAdded?: () => void;
}

export function AddUrlDialog({
	pod,
	isOpen,
	onClose,
	onUrlAdded,
}: AddUrlDialogProps) {
	const [url, setUrl] = React.useState('');
	const [bulkUrls, setBulkUrls] = React.useState('');
	const [isProcessing, setIsProcessing] = React.useState(false);
	const [urlsAdded, setUrlsAdded] = React.useState(0);
	const { toast } = useToast();
	const convex = useConvex();
	const addUrl = useMutation(api.urls.addUrl);
	const assignToPod = useMutation(api.twitterXPosts.assignToPod);
	const podMode = pod.mode === 'broadcast' ? 'broadcast' : 'swarm';
	const platformLabel = getPodPlatformLabel(pod.platform);
	const modeCapability = getPodModeCapability(pod.platform, podMode);
	const urlLabel =
		podMode === 'broadcast'
			? `Enter ${platformLabel} profile URL`
			: `Enter ${platformLabel} post URL`;
	const urlPlaceholder =
		modeCapability.placeholder ||
		(podMode === 'broadcast'
			? 'https://x.com/username'
			: 'https://x.com/username/status/123456');

	// Check if URL exists - for single URL input
	const checkUrlExists = useQuery(api.urls.checkUrlExists, {
		url,
		podId: podMode === 'swarm' ? pod._id : null,
		profileKey:
			podMode === 'broadcast' ? pod.members[0]?.uploadPostProfileKey : null,
		mode: podMode,
	});

	const checkIfUrlExists = React.useCallback(
		async (urlToCheck: string) => {
			return await convex.query(api.urls.checkUrlExists, {
				url: urlToCheck,
				podId: podMode === 'swarm' ? pod._id : null,
				profileKey:
					podMode === 'broadcast'
						? pod.members[0]?.uploadPostProfileKey
						: null,
				mode: podMode,
			});
		},
		[convex, pod, podMode]
	);

	const handleSubmit = async () => {
		try {
			const trimmedUrl = url.trim();

			const result = await processUrl(trimmedUrl);
			if (result.success) {
				setUrl('');
				toast({
					title: '✨ URL Added ✨',
					description: `Successfully added URL for @${result.username}`,
					duration: 3000,
				});

				if (onUrlAdded) {
					onUrlAdded();
				}
			} else {
				toast({
					title: 'Error',
					description: result.error,
					variant: 'destructive',
					duration: 3000,
				});
			}
		} catch (e) {
			console.error('Error adding URL:', e);
			toast({
				title: 'Error',
				description:
					e instanceof Error ? e.message : 'Failed to process URL',
				variant: 'destructive',
				duration: 3000,
			});
		}
	};

	const processUrl = React.useCallback(
		async (urlToProcess: string) => {
			try {
				const trimmedUrl = urlToProcess.trim();
				const parsed = parsePodTargetUrl(trimmedUrl, {
					platform: pod.platform,
					mode: podMode,
				});
				if (!parsed.success || !parsed.url || !parsed.username) {
					return {
						success: false,
						error: parsed.error || 'Could not parse this URL.',
					};
				}

				// Check for duplicates
				const exists = await checkIfUrlExists(parsed.url);
				if (exists) {
					return {
						success: false,
						error:
							podMode === 'broadcast'
								? '🔄 This profile URL is already in your collection. Try adding a different profile!'
								: '🔄 This post URL is already in your collection. Try adding a different post!',
						isDuplicate: true,
					};
				}

				const urlResult = await addUrl({
					platform: canonicalPodPlatform(pod.platform),
					url: parsed.url,
					username: parsed.username,
					conversationId:
						podMode === 'swarm' ? parsed.conversationId : undefined,
					userId: pod.userId as Id<'users'>,
					podId: pod._id,
					profileKey:
						podMode === 'broadcast'
							? pod.members[0]?.uploadPostProfileKey
							: null,
					isActive: true,
					mode: podMode,
				});

				if (podMode === 'swarm' && urlResult.postId) {
					await assignToPod({
						postId: urlResult.postId as Id<'twitterXPosts'>,
						podId: pod._id,
					});
				}

				setUrlsAdded((prev) => prev + 1);
				return { success: true, username: parsed.username };
			} catch (e) {
				return {
					success: false,
					error:
						e instanceof Error
							? e.message
							: 'Failed to process URL',
				};
			}
		},
		[pod, podMode, checkIfUrlExists, addUrl, assignToPod, setUrlsAdded]
	);

	const handleBulkSubmit = async () => {
		setIsProcessing(true);
		const urls = extractUrlCandidatesFromText(bulkUrls);

		const results = {
			success: 0,
			failed: 0,
			duplicates: 0,
			errors: [] as string[],
		};

		for (const url of urls) {
			const result = await processUrl(url);
			if (result.success) {
				results.success++;
			} else if (result.isDuplicate) {
				results.duplicates++;
			} else {
				results.failed++;
				results.errors.push(`${url}: ${result.error}`);
			}
		}

		setBulkUrls('');
		setIsProcessing(false);

		toast({
			title: '✨ Import Complete ✨',
			description: `Successfully added ${results.success} new URL${results.success !== 1 ? 's' : ''}${
				results.duplicates > 0
					? `\n⚠️ Skipped ${results.duplicates} duplicate${results.duplicates !== 1 ? 's' : ''} (already in collection)`
					: ''
			}${results.failed > 0 ? `\n❌ ${results.failed} failed due to invalid format` : ''}`,
			duration: 5000,
		});

		if (results.success > 0 && onUrlAdded) {
			onUrlAdded();
		}

		if (results.errors.length > 0) {
			console.error('Failed URLs:', results.errors);
		}
	};

	const onDrop = React.useCallback(
		async (acceptedFiles: File[]) => {
			setIsProcessing(true);
			const results = {
				success: 0,
				failed: 0,
				duplicates: 0,
				errors: [] as string[],
			};

			for (const file of acceptedFiles) {
				if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
					toast({
						title: '📁 Wrong File Type',
						description:
							'We need a CSV file - just a simple text file with one URL per line!',
						variant: 'destructive',
						duration: 3000,
					});
					setIsProcessing(false);
					return;
				}

				const text = await file.text();
				const urls = extractUrlCandidatesFromText(text);

				for (const url of urls) {
					const result = await processUrl(url);
					if (result.success) {
						results.success++;
					} else if (result.isDuplicate) {
						results.duplicates++;
					} else {
						results.failed++;
						results.errors.push(`${url}: ${result.error}`);
					}
				}
			}

			setIsProcessing(false);
			toast({
				title: '✨ Import Complete ✨',
				description: `Successfully added ${results.success} new URL${results.success !== 1 ? 's' : ''}${
					results.duplicates > 0
						? `\n⚠️ Skipped ${results.duplicates} duplicate${results.duplicates !== 1 ? 's' : ''} (already in collection)`
						: ''
				}${results.failed > 0 ? `\n❌ ${results.failed} failed due to invalid format` : ''}`,
				duration: 5000,
			});

			if (results.errors.length > 0) {
				console.error('Failed URLs:', results.errors);
			}
		},
		[toast, processUrl, modeCapability.enabled]
	);

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			'text/csv': ['.csv'],
		},
		maxFiles: 1,
		disabled: !modeCapability.enabled,
	});

	const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
		e.preventDefault();
		const clipboardData = e.clipboardData;
		const pastedData = clipboardData.getData('text');

		const urls = extractUrlCandidatesFromText(pastedData).join('\n');

		setBulkUrls((prevUrls) => {
			const currentUrls = prevUrls.trim();
			return currentUrls ? `${currentUrls}\n${urls}` : urls;
		});
	};

	return (
		<Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
			<DialogContent className='max-w-2xl'>
				<DialogHeader>
					<DialogTitle>Add URL to {pod.name}</DialogTitle>
					<AnimatePresence>
						{urlsAdded > 0 && (
							<motion.div
								initial={{ opacity: 0, y: -20 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -20 }}
								className='absolute top-4 right-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-full shadow-lg'
							>
								<motion.span
									key={urlsAdded}
									initial={{ scale: 1.5 }}
									animate={{ scale: 1 }}
									className='font-bold'
								>
									{urlsAdded} URL{urlsAdded !== 1 ? 's' : ''}{' '}
									Added ✨
								</motion.span>
							</motion.div>
						)}
					</AnimatePresence>
				</DialogHeader>
				<div className='space-y-8'>
					{!modeCapability.enabled && (
						<div className='rounded-md border border-orange-300 bg-orange-50 p-4 text-sm text-orange-900 dark:border-orange-900/60 dark:bg-orange-950/40 dark:text-orange-200'>
							<div className='font-semibold'>
								{platformLabel} {podMode} URL entry disabled
							</div>
							<div className='mt-1'>
								{modeCapability.reason}
							</div>
						</div>
					)}
					{/* Single URL Input Section */}
					<div className='space-y-4'>
						<h3 className='text-lg font-semibold'>
							Add Single URL
						</h3>
						<div className='space-y-2'>
							<label className='text-sm font-medium'>
								{urlLabel}
							</label>
							<div className='flex gap-4'>
								<Input
									placeholder={urlPlaceholder}
									value={url}
									onChange={(e) => setUrl(e.target.value)}
									disabled={!modeCapability.enabled}
									className='flex-1'
								/>
								<Button
									onClick={handleSubmit}
									disabled={
										!modeCapability.enabled || !url.trim()
									}
									className='bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transition-all duration-200 shadow-md hover:shadow-lg'
								>
									✨ Add URL
								</Button>
							</div>
						</div>
					</div>

					{/* Bulk Import Section */}
					<div className='space-y-4'>
						<h3 className='text-lg font-semibold'>
							Bulk Import URLs
						</h3>

						{/* CSV Drop Zone */}
						<div className='space-y-2'>
							<label className='text-sm font-medium'>
								Upload CSV File
							</label>
							<div
								{...getRootProps()}
								className={cn(
									'border-2 border-dashed rounded-lg p-6 text-center transition-colors',
									isDragActive
										? 'border-pink-500 bg-pink-50 dark:bg-pink-900/20'
										: 'border-gray-300 dark:border-gray-700 hover:border-pink-500 cursor-pointer'
								)}
							>
								<input {...getInputProps()} />
								<div className='space-y-4'>
									<UploadIcon />
									<div className='space-y-2'>
										<p className='text-sm font-medium'>
											{isDragActive
												? 'Drop the CSV file here...'
												: 'Drag & drop a CSV file here, or click to select'}
										</p>
										<p className='text-xs text-gray-500'>
											CSV file should contain one URL per
											line
										</p>
										{modeCapability.helperText && (
											<p className='text-xs text-gray-500'>
												{modeCapability.helperText}
											</p>
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Paste URLs Section */}
						<div className='space-y-2'>
							<label className='text-sm font-medium'>
								Or Paste URLs (paste from spreadsheet or one per
								line)
							</label>
							<Textarea
								placeholder={`${urlPlaceholder}\n${urlPlaceholder}`}
								value={bulkUrls}
								onChange={(e) => setBulkUrls(e.target.value)}
								onPaste={handlePaste}
								disabled={!modeCapability.enabled}
								rows={5}
								className='font-mono'
							/>
							<p className='text-xs text-gray-500 mt-1'>
								Pro tip: You can copy multiple URLs from a
								spreadsheet and paste them directly here!
							</p>
							{bulkUrls.trim() && (
								<Button
									onClick={handleBulkSubmit}
									disabled={
										isProcessing || !modeCapability.enabled
									}
									className='mt-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white transition-all duration-200 shadow-md hover:shadow-lg'
								>
									{isProcessing
										? '⏳ Processing...'
										: '🚀 Import URLs'}
								</Button>
							)}
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant='outline' onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
